import { useCallback, useRef, useState } from "react";
import { useIsTouch, usePrefersReducedMotion } from "./useBreakpoint";

interface TiltOptions {
  /** Max rotation in degrees on each axis. */
  max?: number;
  /** Lift towards the viewer while hovered, in px. */
  lift?: number;
  /** Scale applied while hovered. */
  scale?: number;
}

/**
 * Pointer-reactive 3D tilt. Also publishes `--mx` / `--my` (0-100) so the
 * `.holo-surface` sheen tracks the cursor. No-ops on touch and for users who
 * asked for reduced motion.
 */
export function useTilt({ max = 12, lift = 18, scale = 1.03 }: TiltOptions = {}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);
  const isTouch = useIsTouch();
  const reduced = usePrefersReducedMotion();
  const enabled = !isTouch && !reduced;

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (!el || !enabled) return;
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      el.style.setProperty("--mx", String(Math.round(px * 100)));
      el.style.setProperty("--my", String(Math.round(py * 100)));
      el.style.transform =
        `perspective(900px) rotateY(${(px - 0.5) * max * 2}deg) ` +
        `rotateX(${(0.5 - py) * max * 2}deg) translateZ(${lift}px) scale(${scale})`;
    },
    [enabled, lift, max, scale],
  );

  const onPointerEnter = useCallback(() => {
    if (!enabled) return;
    setActive(true);
    const el = ref.current;
    if (el) el.style.transition = "transform 120ms ease-out";
  }, [enabled]);

  const onPointerLeave = useCallback(() => {
    setActive(false);
    const el = ref.current;
    if (!el) return;
    el.style.transition = "transform 420ms cubic-bezier(0.16, 1, 0.3, 1)";
    el.style.transform = "perspective(900px) rotateY(0deg) rotateX(0deg) translateZ(0) scale(1)";
    el.style.setProperty("--mx", "50");
    el.style.setProperty("--my", "50");
  }, []);

  return {
    ref,
    active,
    enabled,
    tiltProps: enabled ? { onPointerMove, onPointerEnter, onPointerLeave } : {},
  };
}
